import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import ThoughtsDialog from './ThoughtsDialog';
import LeadValidator from './LeadValidator';
import { createThought } from '../../store/actions/thoughtsActions';
import { createVideo } from '../../store/actions/videosActions';
import { createPodcast } from '../../store/actions/podcastsActions';
import { createArticle } from '../../store/actions/articlesActions';
import { addMorePoints } from '../../store/actions/freshCardActions';
import firebase from '../../store/firebase'

/**
 * Wrapper component for ThoughtsDialog that adds lead validation against company values
 */
const ThoughtsDialogWrapper = (props) => {
  const [showValidator, setShowValidator] = useState(false);
  const [leadToValidate, setLeadToValidate] = useState(null);
  
  // Function to be passed to ThoughtsDialog (respects company AI filter toggle)
  const handleSubmitPost = async (e, leadData) => {
    e.preventDefault();
    const companyID = props.userdata.companyID;
    let enabled = true;
    try {
      const companySnap = await firebase.database.ref(`company-settings/${companyID}/lead_ai_filter_enabled`).once('value');
      if (companySnap.exists()) {
        enabled = !!companySnap.val();
      } else {
        const globalSnap = await firebase.database.ref('content-settings/lead_ai_filter_enabled').once('value');
        enabled = globalSnap.exists() ? !!globalSnap.val() : true;
      }
    } catch (err) {
      enabled = true;
    }

    if (enabled) {
      setLeadToValidate(leadData);
      setShowValidator(true);
      return;
    }

    // AI filter disabled: submit immediately
    if (leadData) {
      props.createThought(leadData);
      if (leadData.postInternalGlobal === 'postILead') {
        if (leadData.videourl?.videourl || (leadData.videosObj && Object.keys(leadData.videosObj).length > 0)) {
          processVideoUpload(leadData);
        }
        if (leadData.podcasturl?.podcasturl || (leadData.podcastsObj && Object.keys(leadData.podcastsObj).length > 0)) {
          processPodcastUpload(leadData);
        }
        if (leadData.articlesObj && Object.keys(leadData.articlesObj).length > 0) {
          processArticleUpload(leadData);
        }
      }
      if (leadData.execmember === true && props.editing === false) {
        handleAddPoints(leadData);
      }
    }

    props.handleClose();
  };
  
  // Close validator without submitting
  const handleCloseValidator = () => {
    setShowValidator(false);
    setLeadToValidate(null);
  };
  
  // Actually submit the post after validation
  const handleSubmitValidated = () => {
    if (leadToValidate) {
      // Call the original submit function in ThoughtsDialog
      props.createThought(leadToValidate);
      
      if (leadToValidate.postInternalGlobal === 'postILead') {
        // Handle video upload if needed
        if (leadToValidate.videourl.videourl || 
            (leadToValidate.videosObj && Object.keys(leadToValidate.videosObj).length > 0)) {
          processVideoUpload(leadToValidate);
        }
        
        // Handle podcast upload if needed
        if (leadToValidate.podcasturl.podcasturl || 
            (leadToValidate.podcastsObj && Object.keys(leadToValidate.podcastsObj).length > 0)) {
          processPodcastUpload(leadToValidate);
        }
        
        // Handle article upload if needed
        if (leadToValidate.articlesObj && Object.keys(leadToValidate.articlesObj).length > 0) {
          processArticleUpload(leadToValidate);
        }
      }
      
      // Handle points if the user is an exec member
      if (leadToValidate.execmember === true && props.editing === false) {
        handleAddPoints(leadToValidate);
      }
    }
    
    // Close the validator
    setShowValidator(false);
    setLeadToValidate(null);
    
    // Close the dialog
    props.handleClose();
  };
  
  // Helper functions to process different types of uploads
  const processVideoUpload = (leadData) => {
    // Only process if there's no article
    if (!(leadData.articlesObj && Object.keys(leadData.articlesObj).length > 0)) {
      let vurl;
      let vUploadExternal;
      
      if (leadData.videosObj && Object.keys(leadData.videosObj).length > 0) {
        vUploadExternal = "external";
        vurl = getURL(leadData.videosObj);
      } else {
        vUploadExternal = "upload";
        vurl = leadData.videourl.videourl;
      }
      
      const vbody = {
        ...leadData,
        UploadExternal: vUploadExternal,
        attachment: false,
        author: leadData.subtitle,
        ypointsCurrent: props.userdata.pointsy,
        firstName: props.userdata.firstName,
        journalUserName: props.userdata.firstName,
        lastName: leadData.lastName,
        description: leadData.title,
        url: vurl
      };
      
      props.createVideo(vbody);
    }
  };
  
  const processPodcastUpload = (leadData) => {
    // Only process if there's no article
    if (!(leadData.articlesObj && Object.keys(leadData.articlesObj).length > 0)) {
      let vurl;
      let vUploadExternal;
      
      if (leadData.podcastsObj && Object.keys(leadData.podcastsObj).length > 0) {
        vUploadExternal = "external";
        vurl = getURL(leadData.podcastsObj);
      } else {
        vUploadExternal = "upload";
        vurl = leadData.podcasturl.podcasturl;
      }
      
      const vbody = {
        ...leadData,
        podcastType: leadData.podcastType,
        UploadExternal: vUploadExternal,
        attachment: false,
        author: leadData.subtitle,
        description: leadData.title,
        modalTitle: "Add a new podcast",
        url: vurl
      };
      
      props.createPodcast(vbody);
    }
  };
  
  const processArticleUpload = (leadData) => {
    const vUploadExternal = "external";
    // Prefer the first saved article link/title from attachments
    const firstArticle = Array.isArray(leadData.articlesObj) && leadData.articlesObj.length > 0
      ? leadData.articlesObj[0]
      : null;
    const vurl = firstArticle && firstArticle.url ? firstArticle.url : getURL(leadData.articlesObj);
    const resolvedTitle = (leadData.title && leadData.title.trim().length > 0)
      ? leadData.title
      : (firstArticle && firstArticle.title ? firstArticle.title : 'Article');

    const vbody = {
      // carry over all posting context from the validated thought
      ...leadData,
      // normalize article specific fields (match legacy working payload)
      PodcastExternalRecord: "podcastnone",
      VidUploadExternal: "videonone",
      UploadExternal: vUploadExternal,
      attachment: false,
      articleLink: vurl,
      articleType: 2,
      manualOrExternal: "externalLink",
      // ensure required text fields are populated
      title: resolvedTitle,
      body: resolvedTitle,
      description: resolvedTitle,
      subtitle: leadData.subtitle && leadData.subtitle.trim().length > 0 ? leadData.subtitle : 'none',
      modalTitle: "Add a new article",
      url: vurl,
      // user/company identity
      userID: props.userdata.userID,
      userName: props.userdata.firstName,
      companyID: props.userdata.companyID,
      companyName: props.userdata.companyName,
      author: props.userdata.userID,
      journalUserName: props.userdata.firstName,
      // assist backend indexes used in readers/search
      companyID_subcompetencyid: `${props.userdata.companyID}_${leadData.subcompetencyid || ''}`
    };

    if (vbody.articleLink) {
      props.createArticle(vbody);
    }
  };
  
const getpoints = async(uid) => {
    let database = firebase.database;
    let refPath = '/user/' + uid + '/points';

    try {
      const snapshot = await database.ref(refPath).once('value');
      
      if (snapshot.exists()) {
        return snapshot.val(); // Returns the value of 'points'
      } else {
        
        return null;
      }
    } catch (error) {
      
      return null;
    }
  }

  const handleAddPoints = async (leadData) => {
    const realpoints = await getpoints(props.userdata.uid); 
    const safeCurrentPoints = (realpoints !== null && realpoints !== undefined)
      ? realpoints
      : (props.userdata.points !== undefined ? props.userdata.points : 0);
    const safeYPoints = (props.userdata.pointsy !== undefined ? props.userdata.pointsy : 0);
    // Resolve creation reward from global LC settings (fallback to 0.07 LC)
    let creationPoints = 7; // default (0.07 LC)
    try {
      const { getActiveLcSettings, POINTS_PER_LC } = require('../../utils/lc_settings');
      const s = getActiveLcSettings();
      creationPoints = Math.round(Number(s.leadCreationLc || 0.07) * POINTS_PER_LC);
    } catch (e) {}
    const params = {
      uid: props.userdata.uid,
      userID: props.userdata.userID,
      pointsCurrent: safeCurrentPoints,
      ypointsCurrent: safeYPoints,
      pointsAdded: creationPoints,
      trailID: makeid(10),
      companyName: props.userdata.companyName,
      firstName: props.userdata.firstName,
      lastName: props.userdata.lastName,
      title: leadData.title,
      actionType: leadData.postInternalGlobal + ' added',
      actionDesc: leadData.postInternalGlobal
    };
    
    props.addMorePoints(params);
    
    // Wait for points to be added before refreshing
    await new Promise(resolve => setTimeout(resolve, 7000));
    //window.location.reload();
  };
  
  // Helper functions
  const getURL = (props) => {
    for (let x in props) {
      return props[x].url;
    }
  };
  
  const makeid = (length) => {
    var result = '';
    var characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    var charactersLength = characters.length;
    for (var i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
  };
  
  return (
    <>
      <ThoughtsDialog 
        {...props}
        submitPost={handleSubmitPost}
      />
      {showValidator && leadToValidate && (
        <LeadValidator
          open={showValidator}
          lead={leadToValidate}
          companyID={props.userdata.companyID}
          onClose={handleCloseValidator}
          onSubmit={handleSubmitValidated}
        />
      )}
    </>
  );
};

ThoughtsDialogWrapper.propTypes = {
  createThought: PropTypes.func.isRequired,
  createVideo: PropTypes.func,
  createPodcast: PropTypes.func,
  createArticle: PropTypes.func,
  addMorePoints: PropTypes.func,
  handleClose: PropTypes.func.isRequired,
  userdata: PropTypes.object.isRequired,
  editing: PropTypes.bool
};

const mapDispatchToProps = (dispatch) => {
  return {
    createThought: (params) => dispatch(createThought(params)),
    createVideo: (params) => dispatch(createVideo(params)),
    createPodcast: (params) => dispatch(createPodcast(params)),
    createArticle: (params) => dispatch(createArticle(params)),
    addMorePoints: (params) => dispatch(addMorePoints(params))
  };
};

export default connect(null, mapDispatchToProps)(ThoughtsDialogWrapper); 